import {Routing} from 'nutshell-core/routing';

import {writeLocalStorage} from '../../util/localStorage';

import {stripUndefinedProperties} from './strip-undefined-properties';

/**
 * Thunk, returns a function to update the query parameters
 *
 * @param  {string} reportPath   - Name of the report
 * @param  {object} oldUrlParams - Current page's url parameters
 * @param  {function} navigate   - React-router's replace or push function
 * @return {function}              Function to change query params of page
 */
export function updateUrlParam(
    reportPath: string,
    oldUrlParams: any,
    navigate: (...args: any) => any,
    persistToLocalStorageKey?: string
) {
    /**
     * This will then call react-router's replace method with the new search
     * value generated from report params.
     *
     * Takes an object of parameters for a given key, merges it with existing
     * @param  {Object} newParams           - New url parameters (i.e. {report: {gap: 'm'})
     * @param  {boolean} [shouldMerge]      - Should each parameter object be merged to the corresponding existing param object, or replace it entirely?
     * @return {void}
     */
    return function (newParams: any, shouldMerge?: boolean) {
        let params;
        if (shouldMerge) {
            params = Object.keys(newParams).reduce(
                (paramAccum, paramKey) => {
                    paramAccum[paramKey] =
                        typeof newParams[paramKey] === 'string'
                            ? newParams[paramKey]
                            : {
                                  ...paramAccum[paramKey],
                                  ...newParams[paramKey],
                              };

                    return paramAccum;
                },
                {...oldUrlParams}
            );
        } else {
            params = {
                ...oldUrlParams,
                ...newParams,
            };
        }

        const cleanParams = stripUndefinedProperties(params);
        const newSearch = Routing.param(cleanParams);

        navigate({
            pathname: reportPath,
            search: `?${newSearch}`,
        });

        if (persistToLocalStorageKey) {
            writeLocalStorage(
                persistToLocalStorageKey,
                stripSearchParamsBeforePeristing(newSearch)
            );
        }
    };
}

function stripSearchParamsBeforePeristing(searchString: string) {
    const params = Routing.deparam(searchString);

    delete params.q;

    return Routing.param(params);
}
